if (!customElements.get('spare-parts-finder')) {
  class SparePartsFinder extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      try {
        this.data = JSON.parse(this.querySelector('[data-spare-parts-data]').textContent);
      } catch {
        return;
      }

      this.typeSelect = this.querySelector('select[name="spare-parts-type"]');
      this.modelSelect = this.querySelector('select[name="spare-parts-model"]');
      this.submitButton = this.querySelector('button');
      this.notice = this.querySelector('[data-spare-parts-notice]');

      if (!this.typeSelect || !this.modelSelect || !this.submitButton) return;

      this.storageKey = 'spare-parts-' + (this.closest('[id]')?.id || 'finder');

      this.modelSelect.disabled = true;
      this.submitButton.disabled = true;

      this._abortController = new AbortController();
      const { signal } = this._abortController;

      this._populateTypes();

      this.typeSelect.addEventListener('change', () => {
        this._onTypeChange();
        this._saveState();
      }, { signal });

      this.modelSelect.addEventListener('change', () => {
        this._onModelChange();
        this._saveState();
      }, { signal });

      this.submitButton.addEventListener('click', () => this._onSubmit(), { signal });

      this._restoreState();
    }

    disconnectedCallback() {
      if (this._abortController) {
        this._abortController.abort();
        this._abortController = null;
      }
      this._initialized = false;
    }

    _populateTypes() {
      if (this.typeSelect.querySelectorAll('option:not([disabled])').length > 0) return;
      if (!this.data.types) return;

      this.data.types
        .slice()
        .sort((a, b) => {
          const aOrder = a.sortOrder ?? 999;
          const bOrder = b.sortOrder ?? 999;
          if (aOrder !== bOrder) return aOrder - bOrder;
          return a.name.localeCompare(b.name, undefined, { numeric: true });
        })
        .forEach((type) => {
          const option = document.createElement('option');
          option.value = type.handle;
          option.textContent = type.name;
          this.typeSelect.appendChild(option);
        });
    }

    _onTypeChange() {
      const selectedHandle = this.typeSelect.value;
      const selectedType = this.data.types.find((t) => t.handle === selectedHandle);

      this.modelSelect.innerHTML = '';
      this._hideNotice();

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.selected = true;
      placeholder.disabled = true;
      this.modelSelect.appendChild(placeholder);

      if (!selectedType) {
        this.modelSelect.disabled = true;
        this.submitButton.disabled = true;
        return;
      }

      const filteredModels = (this.data.models || []).filter(
        (model) => model.typeHandle === selectedHandle
      );

      const orderedHandles = selectedType.orderedHandles || [];
      const orderIndex = new Map(orderedHandles.map((handle, index) => [handle, index]));

      filteredModels
        .sort((a, b) => {
          const aIndex = orderIndex.has(a.handle) ? orderIndex.get(a.handle) : Infinity;
          const bIndex = orderIndex.has(b.handle) ? orderIndex.get(b.handle) : Infinity;

          if (aIndex !== bIndex) return aIndex - bIndex;

          return a.name.localeCompare(b.name, undefined, { numeric: true });
        })
        .forEach((model) => {
          const option = document.createElement('option');
          option.value = model.handle;
          option.textContent = model.name;
          this.modelSelect.appendChild(option);
        });

      this.modelSelect.disabled = filteredModels.length === 0;
      this.submitButton.disabled = true;
    }

    _onModelChange() {
      const selectedModel = this._getSelectedModel();
      const hasCollection = selectedModel && selectedModel.collectionUrl;

      this.submitButton.disabled = !hasCollection;

      if (selectedModel && !hasCollection) {
        this._showNotice();
      } else {
        this._hideNotice();
      }
    }

    _onSubmit() {
      const selectedModel = this._getSelectedModel();

      if (selectedModel && selectedModel.collectionUrl) {
        window.location.href = selectedModel.collectionUrl;
      }
    }

    _getSelectedModel() {
      if (!this.data.models) return null;
      return this.data.models.find((m) => m.handle === this.modelSelect.value) || null;
    }

    _showNotice() {
      if (this.notice) this.notice.hidden = false;
    }

    _hideNotice() {
      if (this.notice) this.notice.hidden = true;
    }

    _saveState() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify({
          type: this.typeSelect.value,
          model: this.modelSelect.value
        }));
      } catch {}
    }

    _restoreState() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey));
        if (!saved || !saved.type) return;

        this.typeSelect.value = saved.type;
        this._onTypeChange();

        if (saved.model) {
          this.modelSelect.value = saved.model;
          this._onModelChange();
        }
      } catch {}
    }
  }

  customElements.define('spare-parts-finder', SparePartsFinder);
}
